import { useState } from "react";
import { ClipLoader } from "react-spinners";
import {
  TutorAdminResponse,
  TutorsService,
  PaymentsService,
} from "client/openapi";

import Notifications from "util/notifications";

function TutorPayrollPlan({
  tutor,
  payrollEnabled,
  setPayrollEnabled,
  isStripeVerified,
  isCheckingStripe,
}: {
  tutor: TutorAdminResponse;
  payrollEnabled: boolean;
  setPayrollEnabled: (newValue: boolean) => void;
  isStripeVerified: boolean;
  isCheckingStripe: boolean;
}) {
  const updateTutorPayrollStatus = async (newValue: boolean) => {
    if (newValue && !isStripeVerified) {
      Notifications.error(
        "Cannot enable payroll until Stripe account is verified"
      );
      return;
    }

    TutorsService.updateTutor({
      tutorId: tutor.id,
      requestBody: { run_payroll_through_tadpole: newValue },
    })
      .then(() => {
        Notifications.success("Tutor payroll status updated successfully");
      })
      .catch(() => {
        Notifications.error("Failed to update tutor payroll status");
        setPayrollEnabled(!newValue);
      });
  };

  const handleGenerateRemediationLink = async () => {
    try {
      const response =
        await PaymentsService.generateStripeAccountRemediationLink({
          tutorId: tutor.id,
        });
      navigator.clipboard.writeText(response);
      Notifications.success(
        "Verification link copied to clipboard, send this to the tutor!"
      );
    } catch (error) {
      Notifications.error(
        "Could not generate verification link. Has the tutor finished creating their Tadpole account?"
      );
    }
  };

  return (
    <div className="credit-container mx-3">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <span>Stripe Account Status:</span>
          {isCheckingStripe ? (
            <ClipLoader size={16} color="#4B5563" />
          ) : (
            <>
              <span
                className={`font-medium ${
                  isStripeVerified ? "text-green-600" : "text-blue-600"
                }`}
              >
                {isStripeVerified ? "Verified" : "Not Verified"}
              </span>
              {!isStripeVerified && (
                <button
                  onClick={handleGenerateRemediationLink}
                  className="ml-2 px-2 py-0.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                >
                  Get Verification Link
                </button>
              )}
            </>
          )}
        </div>
        {isStripeVerified && (
          <div className="flex items-center gap-2">
            <span>Run Payroll Through Tadpole:</span>
            <button
              onClick={() => {
                const newVal = !payrollEnabled;
                setPayrollEnabled(newVal);
                updateTutorPayrollStatus(newVal);
              }}
              className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors
                duration-200 focus:outline-none ${
                  payrollEnabled ? "bg-blue-400" : "bg-gray-300"
                }`}
            >
              <span
                className={`inline-block w-4 h-4 transform bg-white rounded-full
                  transition-transform duration-200 ${
                    payrollEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TutorPayrollPlan;
